import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import './index.css';
import './src/i18n';

// Sentry — only active when DSN is provided (i.e. on Vercel production); silent in dev.
const sentryDsn = (import.meta as any).env?.VITE_SENTRY_DSN as string | undefined;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: (import.meta as any).env?.MODE || 'production',
    release: (import.meta as any).env?.VITE_SENTRY_RELEASE || undefined,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    // Stale-chunk import failures after a deploy are expected and auto-recovered
    // (ErrorBoundary/lazyWithRetry reload). Don't report them as crashes.
    beforeSend(event, hint) {
      const msg = (hint?.originalException as Error)?.message || event.message || '';
      if (
        typeof msg === 'string' &&
        (msg.includes('Failed to fetch dynamically imported module') ||
          msg.includes('Importing a module script failed') ||
          msg.includes('Loading chunk') ||
          msg.includes('Loading CSS chunk') ||
          msg.includes('is not a valid JavaScript MIME type'))
      ) {
        return null;
      }
      return event;
    },
  });
}
// Lazy-loaded route surfaces — keeps the landing/auth animation stack and the
// authed app shell out of the initial entry bundle. Chunk-load failures are
// handled globally by ErrorBoundary (reload on stale deploy).
const App = lazy(() => import('./App'));
const Landing = lazy(() => import('./src/pages/Landing').then((m) => ({ default: m.Landing })));
const Auth = lazy(() => import('./src/pages/Auth').then((m) => ({ default: m.Auth })));
const Register = lazy(() => import('./src/pages/Register').then((m) => ({ default: m.Register })));
const ForgotPassword = lazy(() => import('./src/pages/ForgotPassword').then((m) => ({ default: m.ForgotPassword })));
import { AuthProvider, useAuth } from './src/contexts/AuthContext';
import { ToastProvider, useToast } from './src/contexts/ToastContext';
import { NotificationProvider } from './src/contexts/NotificationContext';
import { UploadProvider, useUpload } from './src/contexts/UploadContext';
import { GenerationProvider, useGeneration } from './src/contexts/GenerationContext';
import { DragDropProvider } from './src/contexts/DragDropContext';
import { ToastContainer } from './components/Toast';
import { ProcessingProgress } from './src/components/ProcessingProgress';
import ErrorBoundary from './components/ErrorBoundary';
import { useTranslation } from 'react-i18next';

// Full page loading spinner
const PageLoader: React.FC = () => (
  <div className="h-screen flex flex-col items-center justify-center bg-background">
    <div className="relative">
      <div className="h-12 w-12 rounded-full border-[3px] border-muted animate-spin border-t-amber-500" />
    </div>
    <p className="mt-4 text-sm text-muted-foreground">Загрузка...</p>
  </div>
);

// Protected route wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Redirect authenticated users away from login
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <PageLoader />;
  }

  if (isAuthenticated) {
    return <Navigate to="/app" replace />;
  }

  return <>{children}</>;
};

// Toast wrapper
const ToastWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { toasts, removeToast } = useToast();
  return (
    <>
      {children}
      <ToastContainer toasts={toasts} onClose={removeToast} />
    </>
  );
};

// Global processing progress (OCR extraction - persists across navigation)
const GlobalProcessingProgress: React.FC = () => {
  const { processingItems, closeProcessingProgress } = useUpload();
  return (
    <ProcessingProgress
      items={processingItems}
      onClose={closeProcessingProgress}
    />
  );
};

// Global download progress (Google Drive downloads - persists across navigation)
const GlobalDownloadProgress: React.FC = () => {
  const { t } = useTranslation();
  const { downloadingItems, closeDownloadProgress } = useUpload();
  return (
    <ProcessingProgress
      items={downloadingItems}
      onClose={closeDownloadProgress}
      title={t('downloadProgress.title', 'Загрузка файлов...')}
      completeTitle={t('downloadProgress.complete', 'Загрузка завершена')}
      failedTitle={t('downloadProgress.failed', 'Ошибка загрузки')}
    />
  );
};

// Global export progress (Google Sheets export - persists across navigation)
const GlobalExportProgress: React.FC = () => {
  const { t } = useTranslation();
  const { exportingItems, closeExportProgress } = useUpload();
  return (
    <ProcessingProgress
      items={exportingItems}
      onClose={closeExportProgress}
      title={t('exportProgress.title', 'Экспорт в таблицу...')}
      completeTitle={t('exportProgress.complete', 'Экспорт завершён')}
      failedTitle={t('exportProgress.failed', 'Ошибка экспорта')}
    />
  );
};

// Global generation progress (Document generation - persists across navigation)
const GlobalGenerationProgress: React.FC = () => {
  const { t } = useTranslation();
  const { items, closeProgress } = useGeneration();
  const { processingItems, downloadingItems, exportingItems } = useUpload();
  // Stack offset: count how many other progress panels are visible below this one
  const activeBelow = [processingItems, downloadingItems, exportingItems].filter(i => i.length > 0).length;
  return (
    <ProcessingProgress
      items={items}
      onClose={closeProgress}
      title={t('generationProgress.title', 'Генерация документов...')}
      completeTitle={t('generationProgress.complete', 'Генерация завершена')}
      failedTitle={t('generationProgress.failed', 'Ошибка генерации')}
      bottomOffset={activeBelow > 0 ? activeBelow * 280 : undefined}
    />
  );
};

// Main router
const AppRouter: React.FC = () => {
  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Auth />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />
      <Route
        path="/app/*"
        element={
          <ProtectedRoute>
            <App />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
};

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element");
}

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <NotificationProvider>
            <UploadProvider>
              <GenerationProvider>
                <ToastProvider>
                  <DragDropProvider>
                    <ToastWrapper>
                      <AppRouter />
                      <GlobalProcessingProgress />
                      <GlobalDownloadProgress />
                      <GlobalExportProgress />
                      <GlobalGenerationProgress />
                    </ToastWrapper>
                  </DragDropProvider>
                </ToastProvider>
              </GenerationProvider>
            </UploadProvider>
          </NotificationProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  </React.StrictMode>
);
